<template>
  <ControlWrapper
    v-bind="control.controlWrapper.value"
    :styles="control.styles"
    :is-focused="control.isFocused.value"
    :applied-options="control.appliedOptions.value"
    :error="control.control.value.errors"
  >
    <h3 :class="control.styles.control.label">{{ control.computedLabel.value }}</h3>
    <WInput
      :id="control.control.value.id + '-input'"
      type="datetime-local"
      :disabled="!control.control.value.enabled"
      :autofocus="control.appliedOptions.value.focus"
      :placeholder="control.appliedOptions.value.placeholder"
      :label="
        checkTheme(
          'volkswagen',
          'bentley',
          'vwg',
          'audi',
          'mercedes',
          'jlr',
          'common',
          'cinch',
          'crashdaddy',
          'tesla',
          'aa',
          'suzuki'
        )
          ? 'Date and time'
          : ''
      "
      class="widget-date-time-input"
      :postfix="isDatePickerIndicatorSupported ? themeIcons.calendar : ''"
      :max="max"
      :min="min"
      v-model="selectedDate"
      @focus="setFocus(true)"
      @change="onInputDateChange(selectedDate)"
      @blur="setFocus(false)"
      :error="!selectedDate ? 'This field is required' : errors['date'] || ''"
    />
  </ControlWrapper>
</template>

<script setup lang="ts">
  import { useSupported } from '@vueuse/core';
  import { ControlElement } from '@jsonforms/core';
  import { rendererProps, useJsonFormsControl } from '@jsonforms/vue';
  import { default as ControlWrapper } from './ControlWrapper.vue';
  import { useWidgetControl, useWidgetError } from '../util';
  import dayjs from 'dayjs';
  import customParseFormat from 'dayjs/plugin/customParseFormat';
  import utc from 'dayjs/plugin/utc';
  import WInput from '../../base/Input.vue';
  import { onBeforeUnmount, ref, nextTick } from 'vue';
  import { checkTheme, themeIcons } from 'src/utils/config';
  import { useDebounceFn } from '@vueuse/core';

  dayjs.extend(customParseFormat);
  dayjs.extend(utc);

  const props = defineProps(rendererProps<ControlElement>());

  function onDateChange(value: string) {
    const currentLocale = new Intl.Locale(Intl.NumberFormat().resolvedOptions().locale);
    if (!dayjs(value).isValid()) {
      errors.value['date'] = `Approximately when did the collision occur?`;
    } else if (dayjs(value).isBefore(min)) {
      errors.value['date'] =
        `Date must be after ${dayjs(min).toDate().toLocaleString(currentLocale, { dateStyle: 'short', timeStyle: 'short' })}`;
    } else if (dayjs(value).isAfter(max)) {
      errors.value['date'] =
        `Date must be before ${dayjs(max).toDate().toLocaleString(currentLocale, { dateStyle: 'short', timeStyle: 'short' })}`;
    } else {
      errors.value['date'] = '';
    }

    return dayjs(value).format(format + 'Z');
  }

  const { onValidate, errors } = useWidgetError();
  const showErrors = ref(false);
  const listener = onValidate(() => {
    showErrors.value = true;
  });

  onBeforeUnmount(listener.off);

  const onInputDateChange = useDebounceFn((value: string) => {
    nextTick(() => {
      control.onChange(onDateChange(value));
    });
  }, 250);

  const isDatePickerIndicatorSupported = useSupported(() =>
    CSS.supports('selector(::-webkit-calendar-picker-indicator)')
  );

  const format = 'YYYY-MM-DDTHH:mm';
  const control = useWidgetControl(useJsonFormsControl(props), onDateChange);
  const initialValue = control.control.value.data;
  const selectedDate = ref(dayjs(initialValue).format(format));

  const min =
    'min' in control.control.value.schema &&
    (typeof control.control.value.schema.min === 'number' || typeof control.control.value.schema.min === 'string')
      ? dayjs().subtract(Number(control.control.value.schema.min), 'days').format(format)
      : dayjs().subtract(30, 'days').format(format);

  const max =
    'max' in control.control.value.schema &&
    (typeof control.control.value.schema.max === 'number' || typeof control.control.value.schema.max === 'string')
      ? dayjs().subtract(Number(control.control.value.schema.max), 'days').format(format)
      : dayjs().format(format);

  function setFocus(value: boolean) {
    setTimeout(() => {
      control.isFocused.value = value;
    }, 50);
  }

  onInputDateChange(selectedDate.value);
</script>
